<template>
  <div>
    <v-row>
      <v-col cols="12" sm="6">
        <h1>{{ $t('project.signed-documents.SignedDocumentsHeader.signedDocuments') }}</h1>
        <p class="mt-3 mb-0 text-body-1 text-accent-lighten-2">
          {{ $t('project.signed-documents.SignedDocumentsHeader.findSignedDocuments') }}
        </p>
      </v-col>
    </v-row>
    <template v-if="isNotarityEnabled">
      <v-row>
        <v-col>
          <SignedDocumentsNotarityBanner />
        </v-col>
      </v-row>
    </template>
    <template v-if="!noData">
      <v-row align="center">
        <v-col cols="auto">
          <SearchTextField v-model="computedSearchQuery"
                          :tooltipText="$t('project.signed-documents.SignedDocumentsHeader.searchPlaceholder')"
          />
        </v-col>
        <v-col>
          <AppButton @click="isFiltersPaneOpen = true">
            <app-icon-ds icon="sliders" size="xs" weight="far" class="mr-2" />
            <span>{{ $t('project.signed-documents.SignedDocumentsHeader.filtersButton') }}</span>
          </AppButton>
          <SignedDocumentsFiltersPane :is-open="isFiltersPaneOpen" @close="isFiltersPaneOpen = false" />
        </v-col>
        <v-col v-if="!noResults" class="text-right">
          <AppTooltip>
            <template v-slot:activator="{ props }">
              <AppButton v-bind="props" icon text
                        @click="$emit('update:display', DISPLAY.grid)"
              >
                <font-awesome-icon :icon="['far', 'border-all']"
                                  :class="{'text-primary': display === DISPLAY.grid}"
                                  style="font-size: 24px;"
                ></font-awesome-icon>
              </AppButton>
            </template>
            <span>{{$t('project.signed-documents.SignedDocumentsHeader.gridDisplay')}}</span>
          </AppTooltip>
          <AppTooltip>
            <template v-slot:activator="{ props }">
              <AppButton v-bind="props" icon text
                        @click="$emit('update:display', DISPLAY.table)"
              >
                <font-awesome-icon :icon="['far', 'list']"
                                  :class="{'text-primary': display === DISPLAY.table}"
                                  style="font-size: 24px;"
                ></font-awesome-icon>
              </AppButton>
            </template>
            <span>{{$t('project.signed-documents.SignedDocumentsHeader.tableDisplay')}}</span>
          </AppTooltip>
        </v-col>
      </v-row>
      <v-row class="align-center">
        <v-col v-if="display === DISPLAY.grid && filteredSignedDocuments.length > 0" cols="12" lg="auto">
          <v-checkbox :model-value="areAllSelectedInCurrentPage"
                      :label="$t('project.signed-documents.SignedDocumentsHeader.selectAll')"
                      :indeterminate="areSomeSelectedInCurrentPage"
                      color="primary"
                      hide-details
                      class="mt-0"
                      @update:model-value="$emit('toggle-all-selection')"
          />
        </v-col>
        <v-col v-if="selectedSignedDocuments.length > 0" cols="12" lg>
          <SignedDocumentsMultiSelectBar :selection="selectedSignedDocuments"
                                        @deselectAll="unselectAllSignedDocuments"
                                        @selectedSignature="selectedSignatures = $event"
          />
        </v-col>
      </v-row>
      <v-row v-if="hasFilters">
        <v-col>
          <SignedDocumentsHeaderFilters />
        </v-col>
      </v-row>
    </template>
  </div>
</template>

<script>
import { debounce } from 'lodash-es'
import { mapState, mapActions, mapGetters } from 'vuex'

import AppTooltip from '@/common/AppTooltip.vue'
import AppButton from '@/common/buttons/AppButton.vue'
import SearchTextField from '@/common/SearchTextField.vue'
import SignedDocumentsNotarityBanner from '@/project/signed-documents/SignedDocumentsNotarityBanner.vue'
import { DISPLAY } from '@/projects/display'

import SignedDocumentsFiltersPane from './pane/SignedDocumentsFiltersPane.vue'
import SignedDocumentsHeaderFilters from './SignedDocumentsHeaderFilters.vue'
import SignedDocumentsMultiSelectBar from './SignedDocumentsMultiSelectBar.vue'

export default {
  name: 'SignedDocumentsHeader',
  inheritAttrs: false,
  components: {
    AppButton,
    AppTooltip,
    SearchTextField,
    SignedDocumentsFiltersPane,
    SignedDocumentsHeaderFilters,
    SignedDocumentsMultiSelectBar,
    SignedDocumentsNotarityBanner,
  },
  inject: [
    'selectedSignedDocuments',
    'unselectAllSignedDocuments',
  ],
  props: {
    display: {
      type: String,
      required: true,
    },
    noData: {
      type: Boolean,
      required: true,
    },
    noResults: {
      type: Boolean,
      required: true,
    },
    areAllSelectedInCurrentPage: {
      type: Boolean,
      required: true,
    },
    areSomeSelectedInCurrentPage: {
      type: Boolean,
      required: true,
    },
  },
  data () {
    return {
      DISPLAY,
      selectAll: false,
      isFiltersPaneOpen: false,
    }
  },
  computed: {
    ...mapState('signedDocuments', ['signedDocuments', 'searchQuery', 'filters']),
    ...mapGetters('signedDocuments', ['filteredSignedDocuments']),
    ...mapGetters('room', ['isNotarityEnabled']),
    selectedSignatures: {
      get () {
        return this.selectedSignedDocuments
      },
      set (val) {
        // TODO : utilisez inject
        this.$emit('selectedSignature', val)
      },
    },
    computedSearchQuery: {
      get () { return this.searchQuery },
      set: debounce(function (val) {
        this.setSearchQuery(val)
      }, 1000),
    },
    hasFilters () {
      return this.filters.startDate || this.filters.endDate || this.filters.signatories.length > 0
    },
  },
  methods: {
    ...mapActions('signedDocuments', ['setSearchQuery']),
  },
}
</script>
